import React from 'react';
import { Link, useParams } from 'react-router-dom';
import classes from './BlogPost.module.css';
import Seo, { SITE_URL } from './components/Seo';
import Header from './components/Header';
import Footer from './components/Footer';
import LotusLogo from './components/LotusLogo';
import StoreButtons from './components/StoreButtons';
import NotFound from './NotFound';
import { useI18n } from './i18n/LanguageContext';
import { localePath } from './i18n/config';
import { useContent } from './content';
import { ContentBlock } from './content/blocks';
import { slugFor } from './content/slugs';

function AppCta() {
  const { t } = useI18n();

  return (
    <aside className={classes.cta}>
      <div className={classes.ctaLogo}>
        <LotusLogo />
      </div>
      <h3 className={classes.ctaTitle}>{t('post.ctaTitle')}</h3>
      <p className={classes.ctaText}>{t('post.ctaText')}</p>
      <StoreButtons compact />
    </aside>
  );
}

function BlogPost() {
  const { slug } = useParams();
  const { t, path, locale, formatDate } = useI18n();
  const { getPostBySlug, getRelatedPosts } = useContent();

  const post = getPostBySlug(slug);

  if (!post) {
    return <NotFound />;
  }

  const related = getRelatedPosts(post.id, 3);
  const url = `${SITE_URL}${localePath(locale, `/blog/${slugFor(post.id, locale)}`)}`;

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.metaDescription,
      datePublished: post.datePublished,
      dateModified: post.dateModified,
      inLanguage: locale,
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      image: `${SITE_URL}/og-image.png`,
      author: { '@type': 'Organization', name: 'Manifestify', url: SITE_URL },
      publisher: {
        '@type': 'Organization',
        name: 'Portal Studio',
        logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo512.png` },
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: t('post.home'),
          item: `${SITE_URL}${localePath(locale, '/')}`,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: t('post.blog'),
          item: `${SITE_URL}${localePath(locale, '/blog')}`,
        },
        { '@type': 'ListItem', position: 3, name: post.title, item: url },
      ],
    },
  ];

  if (post.faq && post.faq.length > 0) {
    jsonLd.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: post.faq.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: item.a },
      })),
    });
  }

  return (
    <div className={classes.page}>
      <Seo
        title={post.metaTitle}
        description={post.metaDescription}
        postId={post.id}
        type="article"
        jsonLd={jsonLd}
      />
      <Header />

      <main className={classes.main}>
        <article className={classes.article}>
          <nav className={classes.breadcrumb} aria-label={t('post.breadcrumbAria')}>
            <Link to={path('/')}>{t('post.home')}</Link>
            <span aria-hidden="true">/</span>
            <Link to={path('/blog')}>{t('post.blog')}</Link>
          </nav>

          <span className={classes.category}>{post.category}</span>
          <h1 className={classes.title}>{post.title}</h1>
          <p className={classes.meta}>
            <time dateTime={post.datePublished}>{formatDate(post.datePublished)}</time>
            <span aria-hidden="true"> · </span>
            {t('blog.readTime', { n: post.readTime })}
          </p>

          <div className={classes.content}>
            {post.content.map((block, i) => (
              <ContentBlock key={i} block={block} renderCta={() => <AppCta />} />
            ))}
          </div>

          {post.faq && post.faq.length > 0 && (
            <section className={classes.faq}>
              <h2>{t('post.faqTitle')}</h2>
              {post.faq.map((item, i) => (
                <details key={i} className={classes.faqItem}>
                  <summary>{item.q}</summary>
                  <p>{item.a}</p>
                </details>
              ))}
            </section>
          )}
        </article>

        <section className={classes.related} aria-label={t('post.relatedAria')}>
          <h2 className={classes.relatedTitle}>{t('post.relatedTitle')}</h2>
          <div className={classes.relatedGrid}>
            {related.map((p) => (
              <Link
                key={p.id}
                to={path(`/blog/${slugFor(p.id, locale)}`)}
                className={classes.relatedCard}
              >
                <span className={classes.relatedCategory}>{p.category}</span>
                <span className={classes.relatedCardTitle}>{p.title}</span>
                <span className={classes.relatedRead}>{t('blog.readTime', { n: p.readTime })}</span>
              </Link>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default BlogPost;
